import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { CalendarClock, ExternalLink, Loader2 } from "lucide-react";
import { Dialog } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { addDaysToDateKey, localDateKey } from "@/lib/datetime";
import { DEFAULT_VISIT_MINUTES, visitMinutesFromJob, type DispatchJob, type DispatchTech } from "./dispatch-types";
import { useDayWindow } from "./day-swimlane";

export interface ScheduleSubmit {
    jobId: string;
    assignedTo: string;
    scheduledStart: string;
    scheduledEnd: string;
}

interface ScheduleJobDialogProps {
    open: boolean;
    onClose: () => void;
    /** The job to schedule. Null hides the dialog. */
    job: DispatchJob | null;
    techs: DispatchTech[];
    /** Pre-selected tech (e.g. from a week-cell drop or the swimlane row). */
    initialTechId?: string | null;
    /** Pre-selected calendar day (YYYY-MM-DD). Falls back to the job's current
     *  day, else today. */
    initialDate?: string | null;
    /** Pre-filled time (HH:MM, 24h). Blank means "start of day". */
    initialTime?: string | null;
    /** Submit handler — the shell routes this through patchJobMutation. */
    onSchedule: (args: ScheduleSubmit) => void;
}

function techName(t: DispatchTech | undefined): string {
    if (!t) return "tech";
    return `${t.first_name ?? ""} ${t.last_name ?? ""}`.trim() || t.email || "tech";
}

/** Minutes for the visit: existing scheduled range, else the modeled
 *  estimate, else the default — clamped to the 30-minute minimum window
 *  (G3 amendment). Mirrors the shell's reschedule math. */
function jobDurationMinutes(job: DispatchJob): number {
    return visitMinutesFromJob(job);
}

function formatDuration(minutes: number): string {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    if (h === 0) return `${m}m`;
    if (m === 0) return `${h}h`;
    return `${h}h ${m}m`;
}

function formatClock(d: Date): string {
    return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

/** Date key (YYYY-MM-DD) → "Jun 18" for the tech-load line. */
function formatDayShort(dateKey: string): string {
    const [y, mo, d] = dateKey.split("-").map((n) => parseInt(n, 10));
    if (!y || !mo || !d) return dateKey;
    return new Date(y, mo - 1, d).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

/**
 * Explicit manual scheduling dialog. Pick a tech, a day, and (optionally) a
 * time; the end is computed from the job's duration. Leaving the time blank
 * places the job at the configured start-of-day so it still lands on the
 * swimlane and drops off the due-soon-unscheduled safety net.
 */
export function ScheduleJobDialog({
    open,
    onClose,
    job,
    techs,
    initialTechId,
    initialDate,
    initialTime,
    onSchedule,
}: ScheduleJobDialogProps) {
    const { startHour } = useDayWindow();
    const [techId, setTechId] = useState("");
    const [dateKey, setDateKey] = useState("");
    const [time, setTime] = useState("");

    // Reset the form whenever a new job opens the dialog.
    useEffect(() => {
        if (!open || !job) return;
        const fromJobDate = job.scheduled_start ? localDateKey(new Date(job.scheduled_start)) : null;
        setTechId(initialTechId ?? job.assigned_to ?? "");
        setDateKey(initialDate ?? fromJobDate ?? localDateKey(new Date()));
        if (initialTime != null) {
            setTime(initialTime);
        } else if (job.scheduled_start) {
            const d = new Date(job.scheduled_start);
            setTime(`${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`);
        } else {
            setTime("");
        }
    }, [open, job, initialTechId, initialDate, initialTime]);

    const minutes = job ? jobDurationMinutes(job) : DEFAULT_VISIT_MINUTES;

    // Compute the concrete start/end from the current form values.
    const computed = useMemo(() => {
        if (!dateKey) return null;
        const [y, mo, d] = dateKey.split("-").map((n) => parseInt(n, 10));
        if (!y || !mo || !d) return null;
        let hh = startHour;
        let mm = 0;
        let timeGiven = false;
        if (time) {
            const m = /^(\d{1,2}):(\d{2})$/.exec(time.trim());
            if (!m) return null;
            hh = Number(m[1]);
            mm = Number(m[2]);
            if (hh > 23 || mm > 59) return null;
            timeGiven = true;
        }
        const start = new Date(y, mo - 1, d, hh, mm, 0, 0);
        const end = new Date(start.getTime() + minutes * 60_000);
        return { start, end, timeGiven };
    }, [dateKey, time, startHour, minutes]);

    // Load awareness: how many jobs the chosen tech already has that day.
    const loadQuery = useQuery({
        queryKey: ["dispatch", "tech-day-load", techId, dateKey],
        queryFn: () => {
            const params = new URLSearchParams({
                assigned_to: techId,
                date_from: dateKey,
                date_to: addDaysToDateKey(dateKey, 1),
                limit: "200",
                status: "scheduled,en_route,arrived,in_progress",
            });
            return api.get<{ jobs: DispatchJob[]; total: number }>(`/api/jobs?${params}`);
        },
        enabled: open && !!techId && !!dateKey,
        staleTime: 30_000,
    });

    const existingCount = useMemo(() => {
        const jobs = loadQuery.data?.jobs ?? [];
        return jobs.filter((j) => j.id !== job?.id).length;
    }, [loadQuery.data, job?.id]);

    if (!job) return null;

    const selectedTech = techs.find((t) => t.id === techId);
    const canSubmit = !!techId && !!dateKey && computed != null;

    const submit = () => {
        if (!canSubmit || !computed) return;
        onSchedule({
            jobId: job.id,
            assignedTo: techId,
            scheduledStart: computed.start.toISOString(),
            scheduledEnd: computed.end.toISOString(),
        });
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            title="Schedule Job"
            headerActions={
                <Button variant="outline" size="sm" className="h-8" asChild>
                    <Link to={`/jobs/${job.id}`} onClick={onClose}>
                        <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
                        View Job
                    </Link>
                </Button>
            }
        >
            <div className="space-y-4">
                <div className="rounded-lg border bg-background px-3 py-2">
                    <div className="font-medium truncate">{job.title}</div>
                    <div className="text-xs text-foreground/70 truncate">
                        {[job.customer_name, job.site_label].filter(Boolean).join(" · ") || "—"}
                    </div>
                </div>

                <div className="space-y-1.5">
                    <Label htmlFor="schedule-tech">Technician *</Label>
                    <Select id="schedule-tech" value={techId} onChange={(e) => setTechId(e.target.value)}>
                        <option value="">Select a technician…</option>
                        {techs.map((t) => (
                            <option key={t.id} value={t.id}>
                                {techName(t)}
                            </option>
                        ))}
                    </Select>
                </div>

                <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                        <Label htmlFor="schedule-date">Date *</Label>
                        <Input
                            id="schedule-date"
                            type="date"
                            value={dateKey}
                            onChange={(e) => setDateKey(e.target.value)}
                        />
                    </div>
                    <div className="space-y-1.5">
                        <Label htmlFor="schedule-time">Time</Label>
                        <Input
                            id="schedule-time"
                            type="time"
                            value={time}
                            onChange={(e) => setTime(e.target.value)}
                        />
                    </div>
                </div>

                <div className="rounded-lg border bg-muted/40 px-3 py-2 text-sm space-y-1">
                    <div className="flex items-center gap-2 text-foreground/80">
                        <CalendarClock className="h-4 w-4 shrink-0" />
                        {computed ? (
                            <span>
                                {formatClock(computed.start)} – {formatClock(computed.end)}{" "}
                                <span className="text-foreground/60">({formatDuration(minutes)})</span>
                            </span>
                        ) : (
                            <span className="text-foreground/60">Pick a date and a valid time</span>
                        )}
                    </div>
                    {computed && !computed.timeGiven && (
                        <p className="text-xs text-foreground/60">
                            No specific time — placed at the start of the day ({String(startHour).padStart(2, "0")}:00).
                        </p>
                    )}
                    {(job.asset_count ?? 0) > 0 && (
                        <p className="text-xs text-foreground/60">
                            {job.asset_count} device{job.asset_count === 1 ? "" : "s"} on this job.
                        </p>
                    )}
                    {techId && dateKey && (
                        <p className="text-xs text-foreground/60">
                            {loadQuery.isLoading
                                ? "Checking that day's load…"
                                : `${techName(selectedTech)} has ${existingCount} job${
                                      existingCount === 1 ? "" : "s"
                                  } scheduled ${formatDayShort(dateKey)}.`}
                        </p>
                    )}
                </div>

                <div className="flex justify-end gap-2 pt-1">
                    <Button variant="outline" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button disabled={!canSubmit} onClick={submit}>
                        {loadQuery.isLoading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
                        Schedule
                    </Button>
                </div>
            </div>
        </Dialog>
    );
}
